'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Edit3, Eye, Trash2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Report {
  id: string;
  title: string;
  platform: string | null;
  format: string | null;
  status: string | null;
  created_at: string;
  word_count: number | null;
  tone: string | null;
}

const platformBadge: Record<string, string> = {
  LI: 'platform-badge-li',
  IG: 'platform-badge-ig',
  X: 'platform-badge-x',
  FB: 'platform-badge-fb',
};

const statusStyle: Record<string, string> = {
  'Freigegeben': 'bg-green-950/40 text-green-400 border-green-800/40',
  'Entwurf': 'bg-surface-2 text-muted border-border',
  'KI-generiert': 'bg-accent/10 text-accent border-accent/30',
  'Bearbeitet': 'bg-blue-950/30 text-blue-400 border-blue-800/40',
  'Geplant': 'bg-purple-950/30 text-purple-400 border-purple-800/40',
  'Veröffentlicht': 'bg-green-950/60 text-green-300 border-green-700/40',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function DashboardRecentPosts() {
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) {
      setReports([]);
      setLoading(false);
      return;
    }

    const fetchReports = async () => {
      try {
        const supabase = createClient();
        const { data, error: fetchError } = await supabase
          .from('reports')
          .select('id, title, platform, format, status, created_at, word_count, tone')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20);

        if (fetchError) throw fetchError;
        setReports(data ?? []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [user]);

  const toggleRow = (id: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === reports.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(reports.map((p) => p.id)));
  };

  return (
    <div className="punk-card">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <p className="section-label">Letzte Beiträge</p>
          <h3 className="text-sm font-600 text-white mt-1">
            {loading ? '…' : `${reports.length} Beiträge · aktuelles Projekt`}
          </h3>
        </div>
        <div className="flex gap-2">
          {selectedRows.size > 0 && (
            <div className="flex gap-2 fade-in">
              <button className="btn-ghost py-1.5 px-3 text-xs text-red-400 border-red-900/40 hover:border-red-500">
                <Trash2 size={12} /> {selectedRows.size} löschen
              </button>
              <button className="btn-ghost py-1.5 px-3 text-xs">
                Exportieren
              </button>
            </div>
          )}
          <Link href="/content-editor" className="btn-ghost py-1.5 px-3 text-xs">
            Alle anzeigen
          </Link>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="flex items-center justify-center py-16">
          <p className="text-red-400 text-sm mono">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && reports.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
            <Edit3 size={20} className="text-accent" />
          </div>
          <p className="text-white font-500 text-sm mb-1">No distortion detected yet.</p>
          <p className="text-muted text-xs">Start your first report to see results.</p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && reports.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 w-10">
                    <input
                      type="checkbox"
                      className="accent-[#FF5F1F] w-3.5 h-3.5"
                      checked={selectedRows.size === reports.length && reports.length > 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="text-left py-3 px-4 section-label font-600">Titel</th>
                  <th className="text-left py-3 px-4 section-label font-600">Plattform</th>
                  <th className="text-left py-3 px-4 section-label font-600">Format</th>
                  <th className="text-left py-3 px-4 section-label font-600">Tonalität</th>
                  <th className="text-left py-3 px-4 section-label font-600">Status</th>
                  <th className="text-left py-3 px-4 section-label font-600">Wörter</th>
                  <th className="text-left py-3 px-4 section-label font-600">Erstellt</th>
                  <th className="text-right py-3 px-4 section-label font-600">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr
                    key={report.id}
                    className={`border-b border-border/50 hover:bg-surface transition-colors group ${
                      selectedRows.has(report.id) ? 'bg-accent/5' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        className="accent-[#FF5F1F] w-3.5 h-3.5"
                        checked={selectedRows.has(report.id)}
                        onChange={() => toggleRow(report.id)}
                      />
                    </td>
                    <td className="py-3 px-4 max-w-xs">
                      <span className="text-white font-500 text-sm truncate block" title={report.title}>
                        {report.title}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {report.platform ? (
                        <span className={`status-badge ${platformBadge[report.platform] ?? ''}`}>
                          {report.platform}
                        </span>
                      ) : (
                        <span className="text-xs text-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs text-muted">{report.format ?? '—'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs text-muted">{report.tone ?? '—'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`status-badge border ${statusStyle[report.status ?? ''] ?? 'text-muted'}`}>
                        {report.status ?? '—'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="mono text-xs text-muted">{report.word_count ?? '—'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="mono text-xs text-muted">{formatDate(report.created_at)}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link href="/content-editor" title="Bearbeiten">
                          <button className="p-1.5 text-muted hover:text-accent transition-colors">
                            <Edit3 size={13} />
                          </button>
                        </Link>
                        <button className="p-1.5 text-muted hover:text-white transition-colors" title="Vorschau">
                          <Eye size={13} />
                        </button>
                        <button className="p-1.5 text-muted hover:text-red-400 transition-colors" title="Löschen">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <span className="mono text-xs text-muted">{reports.length} Beiträge</span>
          </div>
        </>
      )}
    </div>
  );
}